import { PageType } from '@vgtv/api-client/lib/page';

import type { Page } from '@vgtv/api-client/lib/page';
import type { ComponentId } from '@vgtv/api-client/lib/component';
import type { SvpAssetId } from '@vgtv/api-client/lib/svp_asset';
import type { SvpCategoryId } from '@vgtv/api-client/lib/svp_category';
import type { CnpTagId } from '@vgtv/api-client/lib/cnp_tag';
import type { CnpStoryId } from '@vgtv/api-client/lib/cnp_story';
import type { Vendor } from '../../types';
import type { FetchingState } from './index';

type State = { fetching: FetchingState };

const selectIsPageFetching = (
  state: State,
  identifier: SvpCategoryId | CnpTagId | CnpStoryId | string,
  pageType: Page['type'],
  vendor: Vendor
) => {
  if (pageType === PageType.CATEGORY) {
    return Boolean(
      state.fetching.pageConfiguration[vendor].byCategoryId[
        identifier as SvpCategoryId
      ]
    );
  }

  if (pageType === PageType.TAG) {
    return Boolean(
      state.fetching.pageConfiguration[vendor].byTagId[identifier as CnpTagId]
    );
  }

  if (pageType === PageType.STORY) {
    return Boolean(
      state.fetching.pageConfiguration[vendor].byStoryId[
        identifier as CnpStoryId
      ]
    );
  }

  if (pageType === PageType.CUSTOM) {
    return Boolean(
      state.fetching.pageConfiguration[vendor].bySlug[identifier as string]
    );
  }

  return false;
};

const selectIsPageFetched = (
  state: State,
  identifier: SvpCategoryId | CnpTagId | CnpStoryId | string,
  pageType: Page['type'],
  vendor: Vendor
) => {
  let status;

  if (pageType === PageType.CATEGORY) {
    status =
      state.fetching.pageConfiguration[vendor].byCategoryId[
        identifier as SvpCategoryId
      ];
  }

  if (pageType === PageType.TAG) {
    status =
      state.fetching.pageConfiguration[vendor].byTagId[identifier as CnpTagId];
  }

  if (pageType === PageType.STORY) {
    status =
      state.fetching.pageConfiguration[vendor].byStoryId[
        identifier as CnpStoryId
      ];
  }

  if (pageType === PageType.CUSTOM) {
    status =
      state.fetching.pageConfiguration[vendor].bySlug[identifier as string];
  }

  return status !== undefined;
};

const selectIsComponentFetching = (state: State, componentId: ComponentId) => {
  return Boolean(state.fetching.componentConfiguration[componentId]);
};

const selectIsComponentFetched = (state: State, componentId: ComponentId) => {
  return state.fetching.componentConfiguration[componentId] === false;
};

const selectIsAssetFetching = (
  state: State,
  assetId: SvpAssetId,
  vendor: Vendor
) => {
  return Boolean(state.fetching.assets[vendor][assetId]);
};

const selectIsAssetFetched = (
  state: State,
  assetId: SvpAssetId,
  vendor: Vendor
) => {
  return state.fetching.assets[vendor][assetId] === false;
};

const selectIsWatchtimeFetching = (
  state: State,
  assetId: SvpAssetId,
  vendor: Vendor
) => {
  return state.fetching.watchtimes.assets[vendor][assetId] === true;
};

const selectIsWatchtimeFetched = (
  state: State,
  assetId: SvpAssetId,
  vendor: Vendor
) => {
  return state.fetching.watchtimes.assets[vendor][assetId] === false;
};

const selectIsSeriesWatchtimeFetching = (
  state: State,
  categoryId: SvpCategoryId,
  vendor: Vendor
) => {
  return state.fetching.watchtimes.series[vendor][categoryId] === true;
};

const selectIsSeriesWatchtimeFetched = (
  state: State,
  categoryId: SvpCategoryId,
  vendor: Vendor
) => {
  return state.fetching.watchtimes.series[vendor][categoryId] === false;
};

export {
  selectIsPageFetching,
  selectIsPageFetched,
  selectIsComponentFetching,
  selectIsComponentFetched,
  selectIsAssetFetched,
  selectIsAssetFetching,
  selectIsWatchtimeFetching,
  selectIsWatchtimeFetched,
  selectIsSeriesWatchtimeFetching,
  selectIsSeriesWatchtimeFetched,
};
